import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from "@mui/material/CircularProgress";
import {getMatchInfo} from "actions/matchingAction";
import {convertUrl} from "common/helpers";
import {useSetTranslation} from "common/useSetTranslation";
import Dialog from "components/redesign/Dialog";
import {Check} from "lucide-react";
import {get, indexOf, isEmpty, isNil, map, sortBy} from 'lodash';
import React, {useEffect} from "react";
import {connect} from "react-redux";
import {useTranslation} from "react-i18next";
import MTMarkdown from "./MTMarkdown";
import defaultMatchingIcon from 'common/assets/defaultMatchingIcon.png';
import {color, font, match as matchTokens, radius, type} from "common/tokens";

const useStyles = makeStyles({
  name: {
    ...type.cardHeading,
    color: color.TEXT,
  },
  statusBanner: {
    background: matchTokens.CARD_MUTED_BG,
    borderRadius: radius.CONTROL,
    padding: '10px 12px',
    marginBottom: 8,
    ...type.meta,
    fontWeight: font.MEDIUM,
    color: color.TEXT_MUTED,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px 0',
    color: color.TEXT_MUTED,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 0',
    borderBottom: `1px solid ${matchTokens.CARD_BORDER}`,
    "&:last-child": {borderBottom: 0},
  },
  iconWrapper: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    background: matchTokens.AVATAR_BG,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    width: 15,
    height: 15,
    objectFit: 'contain',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  criteriaLabel: {
    ...type.chipSmall,
    color: matchTokens.ACCENT,
  },
  answers: {
    marginTop: 4,
    ...type.meta,
    color: color.TEXT,
  },
  matched: {
    fontWeight: font.BOLD,
  },
  helpText: {
    marginTop: 8,
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
  },
  matchBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
    background: matchTokens.BADGE_SUCCESS_BG,
    color: matchTokens.BADGE_SUCCESS_FG,
    borderRadius: radius.PILL,
    padding: '3px 7px',
    ...type.chipSmall,
  },
});

const MatchCriteriaRow = ({criteria}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const answers = get(criteria, 'answer', []);
  const isMatchedAnswer = answer => indexOf(get(criteria, 'matchedAnswer'), answer) >= 0;
  const isGroupInfo = get(criteria, 'key') === 'group-info';
  const icon = isNil(criteria.icon) ? defaultMatchingIcon : convertUrl(criteria.icon);
  const helpText = st(criteria.helpText, criteria.localeHelpText);

  return (
    <div className={classes.row}>
      <div className={classes.iconWrapper}>
        <img src={icon} alt={''} className={classes.icon}/>
      </div>
      <div className={classes.body}>
        <div className={classes.criteriaLabel}>{st(criteria.matchingCriteria, criteria.localeMatchingCriteria)}</div>
        <div className={classes.answers}>
          {map(answers, (answer, i) => isGroupInfo
            ? <MTMarkdown key={`${answer}-${i}`}>{answer}</MTMarkdown>
            : (
              <span key={`${answer}-${i}`} className={isMatchedAnswer(answer) ? classes.matched : undefined}>
                {answer}{answers.length > 1 && i < answers.length - 1 && ', '}
              </span>
            ))}
        </div>
        {!isEmpty(helpText) && <div className={classes.helpText}>{helpText}</div>}
      </div>
      {criteria.match && (
        <span className={classes.matchBadge}><Check size={10} strokeWidth={3}/>{t('match')}</span>
      )}
    </div>
  );
};

const MatchingInfoModal = ({open, matchId, handleClose, getMatchInfo, matchInfo, fullName, statusMessage}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  useEffect(() => {
    getMatchInfo(matchId)
  }, [matchId]);

  const matchingParameters = sortBy(get(matchInfo, 'criteriaJson'), criteria => criteria.order);
  const about = get(matchInfo, 'about');
  // getMatchInfo stores {} before fetching, so this separates "loading" from
  // "loaded with no criteria" — the latter must still render the bio.
  const isLoading = isEmpty(matchInfo);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={520}
            title={<span className={classes.name}>{fullName}</span>}>
      {Boolean(statusMessage) && <div className={classes.statusBanner}>{statusMessage}</div>}
      {isLoading ? <div className={classes.loading}><CircularProgress size={20} color={'inherit'}/></div> : (
        <>
          {map(matchingParameters, criteria => <MatchCriteriaRow key={criteria.key} criteria={criteria}/>)}
          {!isEmpty(about) && (
            <MatchCriteriaRow criteria={{key: 'about', matchingCriteria: t('about'), answer: [about], match: false}}/>
          )}
        </>
      )}
    </Dialog>
  )
};

const mapStateToProps = ({matching: {matchInfo}}) => ({matchInfo});

export default connect(mapStateToProps, {getMatchInfo})(MatchingInfoModal)
